import { UserRole } from '@/types/enums';

/**
 * Katalog RBAC: definisi resource (menu/modul) × aksi yang dapat diatur Super Admin.
 *
 * Sumber kebenaran untuk MATRIKS yang ditampilkan & default fallback. Nilai aktual
 * disimpan di tabel `role_permissions` (dapat dikonfigurasi). Modul ini hanya lapisan
 * UX; keamanan sebenarnya tetap pada RLS Postgres.
 */

export type RbacAction = 'view' | 'create' | 'update' | 'delete';

export interface RbacResource {
  key: string;
  label: string;
  /** Aksi yang relevan untuk resource ini (yang ditampilkan sebagai kolom centang). */
  actions: RbacAction[];
  /** Deskripsi singkat untuk tooltip/keterangan. */
  description?: string;
}

export const RBAC_ACTIONS: RbacAction[] = ['view', 'create', 'update', 'delete'];

export const ACTION_LABELS: Record<RbacAction, string> = {
  view: 'Lihat',
  create: 'Tambah',
  update: 'Ubah',
  delete: 'Hapus',
};

/** Urutan role pada kolom matriks (kiri → kanan). */
export const RBAC_ROLES: UserRole[] = [
  UserRole.NASABAH,
  UserRole.PENGELOLA,
  UserRole.ADMIN_BANK_SAMPAH,
  UserRole.SUPER_ADMIN,
];

/** Katalog resource — selaras dengan seed migrasi 0010. */
export const RBAC_RESOURCES: RbacResource[] = [
  { key: 'dashboard', label: 'Dashboard', actions: ['view'], description: 'Halaman ringkasan utama' },
  { key: 'nasabah', label: 'Nasabah', actions: ['view', 'create', 'update', 'delete'], description: 'Data & migrasi nasabah' },
  { key: 'pengguna', label: 'Manajemen Pengguna', actions: ['view', 'create', 'update', 'delete'], description: 'Akun staf/pengelola' },
  { key: 'transaksi', label: 'Transaksi / Setoran', actions: ['view', 'create'], description: 'Input setoran & riwayat' },
  { key: 'approval', label: 'Approval Penarikan', actions: ['view', 'update'], description: 'Setujui/tolak penarikan' },
  { key: 'approval_rules', label: 'Aturan Persetujuan', actions: ['view', 'create', 'update', 'delete'], description: 'Aturan persetujuan transaksi (approval rules engine)' },
  { key: 'harga', label: 'Harga & Kategori', actions: ['view', 'create', 'update', 'delete'], description: 'Daftar harga sampah' },
  { key: 'laporan', label: 'Laporan', actions: ['view'], description: 'Statistik & laporan' },
  { key: 'audit', label: 'Audit Trail', actions: ['view'], description: 'Log perubahan data' },
  { key: 'bank_sampah', label: 'Bank Sampah / Cabang', actions: ['view', 'create', 'update', 'delete'], description: 'Kelola cabang & profil bank sampah' },
  { key: 'pengaturan', label: 'Pengaturan Sistem', actions: ['view', 'update'], description: 'Konfigurasi platform' },
  { key: 'penarikan', label: 'Penarikan Saldo', actions: ['view', 'create'], description: 'Ajukan/proses penarikan' },
  { key: 'kas', label: 'Kas & Penjualan', actions: ['view', 'create'], description: 'Buku kas cabang & penjualan sampah ke pengepul' },
  { key: 'statistik', label: 'Statistik & Jejak Karbon', actions: ['view'], description: 'Statistik setoran sampah & karbon tereduksi' },
  { key: 'notifikasi', label: 'Notifikasi & Pengumuman', actions: ['view', 'create', 'update', 'delete'], description: 'Kirim notifikasi/pengumuman ke nasabah atau seluruh pengguna' },
  { key: 'profil', label: 'Profil Sendiri', actions: ['view', 'update'], description: 'Profil pengguna sendiri' },
  { key: 'rbac', label: 'Hak Akses (RBAC)', actions: ['view', 'update'], description: 'Pengaturan hak akses ini' },
];

export const RESOURCE_LABELS: Record<string, string> = Object.fromEntries(
  RBAC_RESOURCES.map((r) => [r.key, r.label]),
);

/** Kunci permission gabungan. */
export function permKey(role: UserRole | string, resource: string, action: RbacAction): string {
  return `${role}.${resource}.${action}`;
}

/**
 * Default fallback (dipakai bila baris DB belum dimuat / belum ada).
 * Mirror seed migrasi 0010. SUPER_ADMIN selalu true (cegah lockout).
 */
export function defaultAllowed(role: UserRole, resource: string, action: RbacAction): boolean {
  if (role === UserRole.SUPER_ADMIN) return true;

  if (role === UserRole.ADMIN_BANK_SAMPAH) {
    if (resource === 'dashboard') return action === 'view';
    if (resource === 'nasabah') return true;
    if (resource === 'pengguna') return true;
    if (resource === 'transaksi') return action === 'view' || action === 'create';
    if (resource === 'approval') return action === 'view' || action === 'update';
    if (resource === 'approval_rules') return true;
    if (resource === 'harga') return true;
    if (resource === 'laporan') return action === 'view';
    if (resource === 'audit') return action === 'view';
    if (resource === 'bank_sampah') return true;
    if (resource === 'pengaturan') return action === 'view' || action === 'update';
    if (resource === 'penarikan') return action === 'view';
    if (resource === 'kas') return action === 'view' || action === 'create';
    if (resource === 'notifikasi') return action === 'view' || action === 'create';
    if (resource === 'profil') return action === 'view' || action === 'update';
    return false;
  }

  if (role === UserRole.PENGELOLA) {
    if (resource === 'dashboard') return action === 'view';
    if (resource === 'nasabah') return true;
    if (resource === 'transaksi') return action === 'view' || action === 'create';
    if (resource === 'approval') return action === 'view' || action === 'update';
    if (resource === 'approval_rules') return action === 'view';
    if (resource === 'harga') return action === 'view';
    if (resource === 'laporan') return action === 'view';
    if (resource === 'audit') return action === 'view';
    if (resource === 'penarikan') return action === 'view';
    if (resource === 'kas') return action === 'view' || action === 'create';
    if (resource === 'notifikasi') return action === 'view' || action === 'create';
    if (resource === 'profil') return action === 'view' || action === 'update';
    return false;
  }

  if (role === UserRole.NASABAH) {
    if (resource === 'dashboard') return action === 'view';
    if (resource === 'transaksi') return action === 'view';
    if (resource === 'penarikan') return action === 'view' || action === 'create';
    if (resource === 'statistik') return action === 'view';
    if (resource === 'harga') return action === 'view';
    if (resource === 'profil') return action === 'view' || action === 'update';
    return false;
  }

  return false;
}
